// @ts-nocheck
import type { App } from 'vue';
import { reactive } from 'vue';
import PublicConversationTab from './routes/dashboard/settings/inbox/settingsPage/PublicConversationTab.vue';
import PublicConversationSettings from './routes/dashboard/settings/account/PublicConversationSettings.vue';
import PublicConversationInboxToggle from './routes/dashboard/settings/inbox/settingsPage/PublicConversationInboxToggle.vue';

const axios = window.axios;

interface PluginSystem {
  registerTab(componentName: string, config: Record<string, unknown>): void;
  registerHook(hookName: string, callback: (...args: any[]) => any): void;
  registerTranslations(i18n: any, translations: Record<string, any>, locale?: string): void;
}

interface InstallContext {
  PluginSystem?: PluginSystem;
  i18n?: any;
}

interface SidebarMenuPayload {
  items?: Array<Record<string, any>>;
  t?: (key: string) => string;
  accountScopedRoute?: (
    name: string,
    params?: Record<string, any>,
    query?: Record<string, any>
  ) => any;
  context?: {
    accountId?: string | number;
  };
}

const getAccountIdFromPath = (): string | null => {
  const matched = window.location.pathname.match(/\/accounts\/(\d+)/);
  return matched?.[1] || null;
};

// ── Inbox enabled cache (reactive so Vue re-evaluates tabs() condition automatically) ──
const _inboxCache: Record<string, boolean> = reactive({});

const setInboxEnabled = (inboxId: string | number, enabled: boolean) => {
  _inboxCache[String(inboxId)] = enabled;
};

const isInboxEnabled = (inboxId: string | number): boolean =>
  _inboxCache[String(inboxId)] === true;

// Expose helpers on window so PublicConversationInboxToggle.vue can update the cache
(window as any).__publicConvLink = { setInboxEnabled, isInboxEnabled };

const registerSidebarMenuHook = (PluginSystem: PluginSystem) => {
  PluginSystem.registerHook(
    'sidebar:menu-items',
    (payload: SidebarMenuPayload) => {
      if (!payload?.items || !Array.isArray(payload.items)) {
        return payload;
      }

      const settingsGroup = payload.items.find(item => item.name === 'Settings');
      if (!settingsGroup || !Array.isArray(settingsGroup.children)) {
        return payload;
      }

      const exists = settingsGroup.children.some(
        child => child.name === 'Settings Public Conversations'
      );

      if (!exists) {
        settingsGroup.children.push({
          name: 'Settings Public Conversations',
          label:
            payload.t?.('PUBLIC_CONVERSATION_LINK.SIDEBAR_LABEL') ||
            'Public conversations',
          icon: 'i-lucide-link',
          activeOn: ['settings_public_conversations'],
          to: payload.accountScopedRoute
            ? payload.accountScopedRoute('settings_public_conversations')
            : {
                name: 'settings_public_conversations',
                params: { accountId: payload.context?.accountId },
              },
        });
      }

      return payload;
    }
  );
};

const registerTranslations = (PluginSystem: PluginSystem, i18n?: any) => {
  if (!i18n) {
    return;
  }

  PluginSystem.registerTranslations(
    i18n,
    {
      PUBLIC_CONVERSATION_LINK: {
        TITLE: 'Public conversations',
        SIDEBAR_LABEL: 'Public conversations',
        SETTINGS: {
          DESCRIPTION:
            'Configure account-level defaults for public conversation links. Inbox settings can override these defaults.',
          ENABLE_ACCOUNT: 'Enable public conversation links for this account',
          ALLOW_ATTACHMENTS: 'Allow attachments in public view',
          HIDE_METADATA: 'Hide technical metadata',
          SAVE: 'Save settings',
          GENERATE: 'Generate public link',
        },
      },
    },
    'en'
  );

  PluginSystem.registerTranslations(
    i18n,
    {
      PUBLIC_CONVERSATION_LINK: {
        TITLE: 'Conversas públicas',
        SIDEBAR_LABEL: 'Conversas públicas',
        SETTINGS: {
          DESCRIPTION:
            'Configure padrões globais de links públicos por conta. As configurações da inbox podem sobrescrever esses padrões.',
          ENABLE_ACCOUNT: 'Ativar links públicos de conversas para esta conta',
          ALLOW_ATTACHMENTS: 'Permitir anexos na visualização pública',
          HIDE_METADATA: 'Ocultar metadados técnicos',
          SAVE: 'Salvar configurações',
          GENERATE: 'Gerar link público',
        },
      },
    },
    'pt_BR'
  );
};

export async function install(
  app: App,
  { PluginSystem, i18n }: InstallContext = {}
) {
  app.component('PublicConversationTab', PublicConversationTab);
  app.component('PublicConversationSettings', PublicConversationSettings);
  app.component('PublicConversationInboxToggle', PublicConversationInboxToggle);

  if (PluginSystem) {
    registerTranslations(PluginSystem, i18n);
    registerSidebarMenuHook(PluginSystem);

    PluginSystem.registerTab('Settings', {
      key: 'publicConversation',
      name: 'Public Link',
      component: PublicConversationTab,
      order: 10000,
      condition: (context: Record<string, any>) => {
        const inboxId = context?.inbox?.id;
        if (!inboxId) return false;
        return isInboxEnabled(inboxId);
      },
    });

    window.dispatchEvent(new Event('chatwoot:sidebar:refresh'));
  }
}

export default { install };
